import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  fetchDateConfiguration, 
  getFallbackDateConfig,
  getCurrentYear,
  getFormattedDate,
  getDateRange,
  isCurrentlyInPeriod,
  getCurrentPeriod
} from '../services/dateConfigService';

const DateConfigContext = createContext();

const CACHE_KEY = 'skiSaleDateConfig';
const CACHE_EXPIRY_KEY = 'skiSaleDateConfigExpiry';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const DateConfigProvider = ({ children }) => {
  const [dateConfig, setDateConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize with cached data
  useEffect(() => {
    const initializeConfig = async () => {
      try {
        // Try to load from cache first
        const cached = loadFromCache();
        if (cached) {
          console.log('Using cached date configuration');
          setDateConfig(cached);
          setLoading(false);
        }

        // Always fetch fresh data in background
        const fresh = await fetchDateConfiguration();
        setDateConfig(fresh);
        setError(null);
        saveToCache(fresh);
        
      } catch (err) {
        console.error('Error initializing date configuration:', err);
        setError(err.message);
        
        // If we don't have cached data, use fallback
        if (!dateConfig) {
          const fallback = getFallbackDateConfig();
          setDateConfig(fallback);
        }
      } finally {
        setLoading(false);
      }
    };

    initializeConfig();
  }, []);

  // Periodic refresh
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const fresh = await fetchDateConfiguration();
        setDateConfig(fresh);
        saveToCache(fresh);
        setError(null);
      } catch (err) {
        console.error('Error refreshing date configuration:', err);
        setError(err.message);
      }
    }, CACHE_DURATION);

    return () => clearInterval(interval);
  }, []);

  const loadFromCache = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      const expiry = localStorage.getItem(CACHE_EXPIRY_KEY);
      
      if (cached && expiry && Date.now() < parseInt(expiry)) {
        return JSON.parse(cached);
      }
    } catch (err) {
      console.error('Error loading from cache:', err);
    }
    return null;
  };

  const saveToCache = (config) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(config));
      localStorage.setItem(CACHE_EXPIRY_KEY, (Date.now() + CACHE_DURATION).toString());
    } catch (err) {
      console.error('Error saving to cache:', err);
    }
  };

  const value = {
    dateConfig,
    loading,
    error,
    
    // Helper functions
    getCurrentSkiYear: () => getCurrentYear(dateConfig),
    formatDate: (dateString, format = 'long') => getFormattedDate(dateString, format),
    formatDateRange: (startDate, endDate, format = 'month-day-year') => getDateRange(startDate, endDate, format),
    isInPeriod: (period) => isCurrentlyInPeriod(dateConfig, period),
    getActivePeriod: () => getCurrentPeriod(dateConfig),
    
    // Convenience getters
    get currentYear() { return getCurrentYear(dateConfig); },
    get dropoff() {
      if (!dateConfig) return { date: 'TBD', formatted: 'TBD' };
      return {
        date: dateConfig.dropoffDate,
        formatted: getFormattedDate(dateConfig.dropoffDate, 'month-day-year')
      };
    },
    get consignment() {
      if (!dateConfig) return { 
        openDate: 'TBD', 
        closeDate: 'TBD', 
        formattedOpen: 'TBD',
        formattedClose: 'TBD',
        range: 'TBD'
      };
      return {
        openDate: dateConfig.consignmentOpenDate,
        closeDate: dateConfig.consignmentCloseDate,
        formattedOpen: getFormattedDate(dateConfig.consignmentOpenDate, 'month-day-year'),
        formattedClose: getFormattedDate(dateConfig.consignmentCloseDate, 'month-day-year'),
        range: getDateRange(dateConfig.consignmentOpenDate, dateConfig.consignmentCloseDate)
      };
    },
    get sale() {
      if (!dateConfig) return { 
        startDate: 'TBD', 
        endDate: 'TBD',
        formattedStart: 'TBD',
        formattedEnd: 'TBD', 
        range: 'TBD'
      };
      return {
        startDate: dateConfig.saleStartDate,
        endDate: dateConfig.saleEndDate,
        formattedStart: getFormattedDate(dateConfig.saleStartDate, 'month-day-year'),
        formattedEnd: getFormattedDate(dateConfig.saleEndDate, 'month-day-year'),
        range: getDateRange(dateConfig.saleStartDate, dateConfig.saleEndDate)
      };
    },
    get pickup() {
      if (!dateConfig) return { 
        startDate: 'TBD', 
        endDate: 'TBD',
        formattedStart: 'TBD',
        formattedEnd: 'TBD',
        range: 'TBD'
      };
      return {
        startDate: dateConfig.pickupStartDate,
        endDate: dateConfig.pickupEndDate,
        formattedStart: getFormattedDate(dateConfig.pickupStartDate, 'month-day-year'),
        formattedEnd: getFormattedDate(dateConfig.pickupEndDate, 'month-day-year'),
        range: getDateRange(dateConfig.pickupStartDate, dateConfig.pickupEndDate)
      };
    }
  };

  return (
    <DateConfigContext.Provider value={value}>
      {children}
    </DateConfigContext.Provider>
  );
};

export const useDateConfig = () => {
  const context = useContext(DateConfigContext);
  if (!context) {
    throw new Error('useDateConfig must be used within a DateConfigProvider');
  }
  return context;
};

export default DateConfigProvider;